/**
 * CTE frame math. No rendering imports.
 *
 * The machine and CAD frame is Z-up. Material axes are aX and aY in the
 * layer, and aZ through the layers (the build direction). A flat print
 * leaves that build axis on global Z.
 */

export const deg = (d) => (d * Math.PI) / 180;

export function rotMatX(a) {
  const c = Math.cos(a), s = Math.sin(a);
  return [[1, 0, 0], [0, c, -s], [0, s, c]];
}
export function rotMatY(a) {
  const c = Math.cos(a), s = Math.sin(a);
  return [[c, 0, s], [0, 1, 0], [-s, 0, c]];
}
export function rotMatZ(a) {
  const c = Math.cos(a), s = Math.sin(a);
  return [[c, -s, 0], [s, c, 0], [0, 0, 1]];
}
export function matMul3(A, B) {
  return A.map((row, i) => [0, 1, 2].map(j => row.reduce((s, _, k) => s + A[i][k] * B[k][j], 0)));
}
export function transpose3(M) {
  return M[0].map((_, j) => M.map(row => row[j]));
}

export function buildRotMatrix(rx, ry, rz) {
  return matMul3(matMul3(rotMatZ(deg(rz)), rotMatY(deg(ry))), rotMatX(deg(rx)));
}

export function rotateCTETensor(ax, ay, az, rx, ry, rz) {
  const R = buildRotMatrix(rx, ry, rz);
  const Rt = transpose3(R);
  const D = [[ax, 0, 0], [0, ay, 0], [0, 0, az]];
  return matMul3(matMul3(R, D), Rt);
}

export function interpolateCTE(dataPoints, temp, axis, cureTemp, postCureScale) {
  if (!dataPoints || !dataPoints.length) return 0;
  const sorted = [...dataPoints].sort((a, b) => a.temp - b.temp);
  let val;
  if (temp <= sorted[0].temp) val = sorted[0][axis];
  else if (temp >= sorted[sorted.length - 1].temp) val = sorted[sorted.length - 1][axis];
  else {
    const hi = sorted.findIndex((p) => p.temp > temp);
    const lo = hi - 1;
    const t = (temp - sorted[lo].temp) / (sorted[hi].temp - sorted[lo].temp);
    val = sorted[lo][axis] + t * (sorted[hi][axis] - sorted[lo][axis]);
  }
  if (cureTemp !== null && postCureScale !== 1.0) {
    const transitionWidth = 20;
    const tgStart = cureTemp - transitionWidth / 2;
    const tgEnd = cureTemp + transitionWidth / 2;
    if (temp >= tgEnd) {
      val *= postCureScale;
    } else if (temp > tgStart) {
      const frac = (temp - tgStart) / transitionWidth;
      val *= 1 + (postCureScale - 1) * frac;
    }
  }
  return val;
}

export function integrateCTE(material, axis, T1, T2, steps = 300) {
  if (T1 === T2) return 0;
  if (material.simple) return material.dataPoints[0][axis] * (T2 - T1);
  const [lo, hi] = T1 < T2 ? [T1, T2] : [T2, T1];
  const sign = T1 < T2 ? 1 : -1;
  const dT = (hi - lo) / steps;
  let sum = 0;
  for (let i = 0; i < steps; i++) {
    const Tmid = lo + (i + 0.5) * dT;
    sum += interpolateCTE(material.dataPoints, Tmid, axis, material.cureTemp, material.postCureScale);
  }
  return sign * sum * dT;
}

// Angle is the tilt of the build axis away from +Z. Azimuth is that tilt's
// direction about Z. R = Rz(azimuth) * Ry(angle), so angle 0 is identity.
export function buildAngleToEuler(angleDeg, azimuthDeg) {
  return { rx: 0, ry: Number(angleDeg) || 0, rz: Number(azimuthDeg) || 0 };
}

// Fixed on the bed (lowest Z) and centred on the footprint, so growth is
// upward and outward rather than from an edge.
export function computeBaseRef(positions) {
  const nv = positions.length / 3;
  let sumX = 0, sumY = 0, minZ = Infinity;
  for (let i = 0; i < nv; i++) {
    sumX += positions[i * 3];
    sumY += positions[i * 3 + 1];
    if (positions[i * 3 + 2] < minZ) minZ = positions[i * 3 + 2];
  }
  return { x: sumX / nv, y: sumY / nv, z: minZ };
}

export function getDispMagnitude(dx, dy, dz) {
  return Math.sqrt(dx * dx + dy * dy + dz * dz);
}

export function meshExtents(positions) {
  if (!positions || positions.length < 9) return null;
  let minX = Infinity, minY = Infinity, minZ = Infinity;
  let maxX = -Infinity, maxY = -Infinity, maxZ = -Infinity;
  const nv = positions.length / 3;
  for (let i = 0; i < nv; i++) {
    const x = positions[i * 3], y = positions[i * 3 + 1], z = positions[i * 3 + 2];
    if (x < minX) minX = x; if (x > maxX) maxX = x;
    if (y < minY) minY = y; if (y > maxY) maxY = y;
    if (z < minZ) minZ = z; if (z > maxZ) maxZ = z;
  }
  if (!Number.isFinite(minX)) return null;
  return { x: maxX - minX, y: maxY - minY, z: maxZ - minZ };
}

// 1-2-5 tick step so chart axes land on round engineering intervals.
export function niceStep(span, target = 6) {
  if (!(span > 0) || !Number.isFinite(span)) return 1;
  const rough = span / Math.max(1, target);
  const pow = 10 ** Math.floor(Math.log10(rough));
  const n = rough / pow;
  const mult = n <= 1 ? 1 : n <= 2 ? 2 : n <= 5 ? 5 : 10;
  return mult * pow;
}

export function niceAxis(min, max, { includeZero = false, target = 6 } = {}) {
  let lo = Number.isFinite(min) ? min : 0;
  let hi = Number.isFinite(max) ? max : 1;
  if (lo > hi) [lo, hi] = [hi, lo];
  if (includeZero) {
    lo = Math.min(lo, 0);
    hi = Math.max(hi, 0);
  }
  if (hi - lo < 1e-12) {
    lo -= 1;
    hi += 1;
  }
  const step = niceStep(hi - lo, target);
  const start = Math.floor(lo / step + 1e-12) * step;
  const end = Math.ceil(hi / step - 1e-12) * step;
  const count = Math.max(1, Math.round((end - start) / step));
  const ticks = [];
  for (let i = 0; i <= count; i++) {
    ticks.push(Math.round((start + i * step) * 1e9) / 1e9);
  }
  return { min: ticks[0], max: ticks[ticks.length - 1], step, ticks };
}
